import React, { useState } from 'react';

const LeftSidebar = ({ 
  shipments, 
  selectedShipment, 
  onShipmentSelect, 
  filters, 
  onFiltersChange,
  dataSource,
  selectedTrialType
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredShipments = shipments.filter(shipment =>
    shipment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shipment.origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shipment.destination.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'truck': return '🚛';
      case 'ship': return '🚢';
      case 'plane': return '✈️';
      default: return '📦';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0">
        {/* Data Source Info */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Data Source:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              dataSource === 'trial' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {dataSource === 'trial' ? 'Trial Data' : 'Mock Data'}
            </span>
          </div>
          {dataSource === 'trial' && selectedTrialType && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Trial Type:</span>
              <span className="text-xs text-gray-600">{selectedTrialType}</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
          
          <div className="space-y-3">
            {/* Mode Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transport Mode</label>
              <select
                value={filters.mode}
                onChange={(e) => onFiltersChange({ ...filters, mode: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Modes</option>
                <option value="truck">Truck</option>
                <option value="ship">Ship</option>
                <option value="plane">Plane</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="in_progress">In Progress</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Anomalies Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anomalies</label>
              <select
                value={filters.anomalies}
                onChange={(e) => onFiltersChange({ ...filters, anomalies: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Shipments</option>
                <option value="with">With Anomalies</option>
                <option value="without">Without Anomalies</option>
              </select>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              placeholder="Search shipments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Shipment List Section */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 sticky top-0 bg-white py-2">Shipments ({filteredShipments.length})</h3>
            
            {filteredShipments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📦</div>
                <p>No shipments found</p>
                <p className="text-sm">Try adjusting your filters or search terms</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredShipments.map((shipment) => (
                  <div
                    key={shipment.id}
                    onClick={() => onShipmentSelect(shipment)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedShipment?.id === shipment.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getModeIcon(shipment.mode)}</span>
                        <span className="font-medium text-gray-900">{shipment.id}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(shipment.status)}`}>
                        {shipment.status.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>From: {shipment.origin}</div>
                      <div>To: {shipment.destination}</div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{shipment.distance} km</span>
                        <span>{shipment.duration} hrs</span>
                        <span>{shipment.anomalies} anomalies</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftSidebar; 